import { MutationFunction, QueryKey, useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { AppError } from '../types/AppError';
import { HookOptions } from '../types/HookOptions';

interface AppMutationOptions<TData, TError, TVariables, TContext> 
  extends Omit<UseMutationOptions<TData, TError, TVariables, TContext>, 'mutationFn'> {
    mutationFn: MutationFunction<TData, TVariables>;
    invalidateKeys?: QueryKey[] | ((data: TData, variables: TVariables) => QueryKey[]);
}

export const useAppMutation = <TData = unknown, TError = AppError, TVariables = void, TContext = unknown>(
    options: AppMutationOptions<TData, TError, TVariables, TContext>,
    hookOptions?: HookOptions<TData, TError, TVariables>
) => {
    const queryClient = useQueryClient()

    return useMutation({
        ...options,
        onSuccess: (data, variables, context) => {
            if (options.invalidateKeys) {
                const keys = typeof options.invalidateKeys === 'function' 
                    ? options.invalidateKeys(data, variables)
                    : options.invalidateKeys
                
                keys.forEach((queryKey) => {
                    queryClient.invalidateQueries({ queryKey })
                })
            }
            
            // @ts-ignore - The type definition seems to mismatch runtime or my understanding, but typical usage is (data, vars, context)
            options.onSuccess?.(data, variables, context)
            hookOptions?.onSuccess?.(data, variables)
        },
        onError: (error, variables, context) => {
             // @ts-ignore
             options.onError?.(error, variables, context)
             hookOptions?.onError?.(error)
        }
    })
}
